import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import client from './api/client';
import { prefetchDestination, forgetDestination } from './api/destinationCache';
import PageHeader from './components/PageHeader';
import CharacterSelector from './components/CharacterSelector';
import './App.css';
import './SeedingPage.css';

function SeedingPage() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Seeding destinations state
  const [destinations, setDestinations] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [structureId, setStructureId] = useState('');
  const [addingDestination, setAddingDestination] = useState(false);
  const [error, setError] = useState('');

  const checkAuthentication = useCallback(async () => {
    try {
      const response = await client.get('/auth/user/');
      return response.data.authenticated;
    } catch (error) {
      console.error('Error checking authentication:', error);
      return false;
    }
  }, []);

  const fetchDestinations = useCallback(async () => {
    try {
      const response = await client.get('/seeding/destinations/');
      setDestinations(response.data.destinations || []);
    } catch (error) {
      console.error('Error fetching destinations:', error);
      if (error.response?.status === 401) {
        // Not authenticated, redirect to home
        navigate('/');
      }
    }
  }, [navigate]);

  const handleAddDestination = async (e) => {
    e.preventDefault();
    if (!structureId.trim()) {
      setError('Please enter a location ID');
      return;
    }

    setAddingDestination(true);
    setError('');

    try {
      const response = await client.post(
        '/seeding/destinations/add/',
        { structure_id: structureId.trim() }
      );

      // Add the new destination to the list
      setDestinations(prev => [...prev, response.data]);
      setShowAddModal(false);
      setStructureId('');
    } catch (error) {
      console.error('Error adding destination:', error);
      setError(error.response?.data?.error || 'Failed to add destination');
    } finally {
      setAddingDestination(false);
    }
  };

  const handleDeleteDestination = async (destinationId) => {
    if (!window.confirm('Are you sure you want to delete this destination?')) {
      return;
    }

    try {
      await client.delete(`/seeding/destinations/${destinationId}/delete/`);

      forgetDestination(destinationId);
      // Remove the destination from the list
      setDestinations(prev => prev.filter(dest => dest.id !== destinationId));
    } catch (error) {
      console.error('Error deleting destination:', error);
      alert('Failed to delete destination');
    }
  };


  // Load auth and destinations in parallel to save a round trip; logged out, the login prompt still shows
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      const [authenticated] = await Promise.all([checkAuthentication(), fetchDestinations()]);
      setIsAuthenticated(authenticated);
      setLoading(false);
    };

    loadData();
  }, [checkAuthentication, fetchDestinations]);

  if (!isAuthenticated && !loading) {
    return (
      <div className="page">
        <PageHeader title="Seeding" />
        <div className="page-status">Log in with your EVE Online character to use the seeding tools.</div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="page">
        <PageHeader title="Seeding" />
        <div className="page-status">Loading destinations...</div>
      </div>
    );
  }

  return (
    <div className="page">
      <PageHeader
        title="Seeding"
        meta={`${destinations.length} destination${destinations.length === 1 ? '' : 's'}`}
      >
        <button onClick={() => setShowAddModal(true)} className="btn btn-primary">
          Add destination
        </button>
      </PageHeader>
      <div className="page-content">
        {/* Character selector for market seeding */}
        <div className="seeding-content">
          <CharacterSelector trackingType="marketSeeding" />
        </div>

        <section className="panel">
          <div className="panel-head">
            <h2>Destinations</h2>
          </div>
          {destinations.length === 0 ? (
            <div className="table-empty">No seeding destinations yet. Use "Add destination" to add one.</div>
          ) : (
            <div className="table-scroll">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Region</th>
                    <th>System</th>
                    <th>Type</th>
                    <th aria-label="Actions"></th>
                  </tr>
                </thead>
                <tbody>
                  {destinations.map((destination) => (
                    <tr key={destination.id}>
                      <td>
                        <Link
                          to={`/seeding/${destination.id}`}
                          onMouseEnter={() => prefetchDestination(destination.id)}
                          onFocus={() => prefetchDestination(destination.id)}
                        >
                          {destination.destination_name}
                        </Link>
                      </td>
                      <td>{destination.destination_region}</td>
                      <td>{destination.destination_system}</td>
                      <td>
                        <span className="pill pill-neutral">
                          {destination.is_structure ? 'Structure' : 'NPC station'}
                        </span>
                      </td>
                      <td className="num">
                        <button
                          onClick={() => handleDeleteDestination(destination.id)}
                          className="btn btn-danger btn-sm row-action"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* Add Destination Modal */}
        {showAddModal && (
          <div className="modal-overlay">
            <div className="modal">
              <div className="modal-header">
                <h3>Add Seeding Destination</h3>
                <button
                  onClick={() => {
                    setShowAddModal(false);
                    setStructureId('');
                    setError('');
                  }}
                  className="icon-btn modal-close"
                >
                  ×
                </button>
              </div>
              <form onSubmit={handleAddDestination}>
                <div className="form-group">
                  <label htmlFor="structureId">Location ID:</label>
                  <input
                    id="structureId"
                    type="text"
                    value={structureId}
                    onChange={(e) => setStructureId(e.target.value)}
                    placeholder="Enter structure (1XXXXXXXXXXXX) or station (6XXXXXXX) ID"
                    disabled={addingDestination}
                    autoFocus
                  />
                  <small className="form-hint">
                    Player structures start with 1 (~13 digits). NPC stations start with 6 (~8 digits).
                  </small>
                </div>
                {error && <div className="error-message">{error}</div>}
                <div className="modal-actions">
                  <button
                    type="button"
                    onClick={() => {
                      setShowAddModal(false);
                      setStructureId('');
                      setError('');
                    }}
                    disabled={addingDestination}
                    className="btn btn-ghost"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={addingDestination || !structureId.trim()}
                    className="btn btn-primary"
                  >
                    {addingDestination ? 'Adding...' : 'Add Destination'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default SeedingPage;